import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CardMedia,
  CardActionArea,
  IconButton,
  Chip,
} from '@mui/material';
import {
  DirectionsCar,
  AttachMoney,
  TrendingUp,
  ChevronLeft,
  ChevronRight,
  Assignment,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { canViewLucros } from '../../utils/permissions';

export default function Dashboard() {
  const { usuario } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalVeiculos: 0,
    veiculosDisponiveis: 0,
    vendasMes: 0,
    lucroMes: 0,
  });
  const [prospeccoes, setProspeccoes] = useState({
    aberto: 0,
    aguardando_banco: 0,
    aguardando_retorno: 0,
    fechado: 0,
    cancelado: 0,
  });
  const [veiculos, setVeiculos] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const veiculosPorPagina = 10;

  const toArray = (value) => (Array.isArray(value) ? value : []);

  const normalizeStatus = (status) => {
    if (!status) return 'disponivel';
    if (typeof status !== 'string') return status;
    return status
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[_-]/g, ' ')
      .trim()
      .replace(/\s+/g, ' ');
  };

  const isDisponivel = (status) => {
    const normalized = normalizeStatus(status);
    return [
      'disponivel',
      'em estoque',
      'estoque',
      'ativo',
    ].includes(normalized);
  };

  useEffect(() => {
    if (canViewLucros(usuario)) {
      loadStats();
    }
    loadVeiculos();
    loadProspeccoes();
  }, []);

  useEffect(() => {
    // Evita página vazia quando a quantidade de veículos muda
    if (currentPage > 0 && currentPage >= Math.ceil(veiculos.length / veiculosPorPagina)) {
      setCurrentPage(0);
    }
  }, [veiculos.length, currentPage]);

  const loadStats = async () => {
    try {
      const [veiculosRes, vendasRes] = await Promise.all([
        api.get('/veiculos'),
        api.get('/vendas'),
      ]);

      const veiculos = toArray(veiculosRes.data.veiculos || veiculosRes.data);
      const vendas = toArray(vendasRes.data);

      const mesAtual = new Date().getMonth();
      const vendasMes = vendas.filter(
        (v) => new Date(v.data_venda).getMonth() === mesAtual
      );

      setStats({
        totalVeiculos: veiculos.length,
        veiculosDisponiveis: veiculos.filter((v) => isDisponivel(v.status)).length,
        vendasMes: vendasMes.length,
        lucroMes: vendasMes.reduce((sum, v) => sum + parseFloat(v.lucro_liquido || 0), 0),
      });
    } catch (error) {
      console.error('Erro ao carregar Estatísticas:', error);
    }
  };

  const loadProspeccoes = async () => {
    try {
      const response = await api.get('/reservas');
      const lista = toArray(response.data);
      const contagem = { aberto: 0, aguardando_banco: 0, aguardando_retorno: 0, fechado: 0, cancelado: 0 };
      lista.forEach((p) => {
        if (contagem[p.status] !== undefined) contagem[p.status]++;
      });
      setProspeccoes(contagem);
    } catch (error) {
      console.error('Erro ao carregar prospecções:', error);
    }
  };

  const loadVeiculos = async () => {
    try {
      const response = await api.get('/veiculos', {
        params: { limit: 1000 }
      });
      const data = toArray(response.data.veiculos || response.data);
      const disponiveis = data.filter((v) => isDisponivel(v.status));
      const ativos = data.filter((v) => !['inativo', 'vendido'].includes(normalizeStatus(v.status)));

      // Prioriza exibir disponíveis; se não houver, mostra ativos para não deixar a galeria vazia
      setVeiculos(disponiveis.length > 0 ? disponiveis : ativos);
    } catch (error) {
      console.error('Erro ao carregar Veículos:', error);
      setVeiculos([]);
    }
  };

  const handleVeiculoClick = (veiculoId) => {
    navigate('/veiculos', { state: { veiculoId } });
  };

  const handleNextPage = () => {
    if ((currentPage + 1) * veiculosPorPagina < veiculos.length) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const veiculosExibidos = veiculos.slice(
    currentPage * veiculosPorPagina,
    (currentPage + 1) * veiculosPorPagina
  );

  const totalPaginas = Math.ceil(veiculos.length / veiculosPorPagina);

  const getStatusColor = (status) => {
    const normalized = normalizeStatus(status);
    const colors = {
      disponivel: 'success',
      reservado: 'warning',
      vendido: 'error',
      manutencao: 'info',
    };
    return colors[normalized] || 'default';
  };

  const getStatusLabel = (status) => {
    const normalized = normalizeStatus(status);
    const labels = {
      disponivel: 'Disponível',
      reservado: 'Reservado',
      vendido: 'Vendido',
      manutencao: 'Manutenção',
    };
    return labels[normalized] || status;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value || 0);
  };

  const StatCard = ({ title, value, icon, color }) => (
    <Card
      elevation={0}
      sx={{
        backgroundColor: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.08)',
      }}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography color="textSecondary" variant="body2" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" component="div" fontWeight="bold">
              {value}
            </Typography>
          </Box>
          <Box
            sx={{
              backgroundColor: color,
              borderRadius: 2,
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  // Verificar permissao para visualizar dashboard
  const podeVerDashboard = canViewLucros(usuario);

  return (
    <Box>
      {/* Boas-vindas */}
      <Paper
        sx={{
          p: 3,
          mb: 3,
          background: 'linear-gradient(135deg, #151515 0%, #2a0f0f 55%, #151515 100%)',
          border: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
          Bem-vindo, {usuario?.nome}!
        </Typography>
        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)', mt: 1 }}>
          {podeVerDashboard
            ? 'Aqui está um resumo do seu negócio e os veículos disponíveis.'
            : 'Confira os veículos disponíveis para venda.'}
        </Typography>
      </Paper>

      {/* Cards de Estatísticas - Apenas para Admin e Gerente */}
      {podeVerDashboard && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total de Veículos"
              value={stats.totalVeiculos}
              icon={<DirectionsCar sx={{ color: 'white', fontSize: 32 }} />}
              color="#1976d2"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Disponíveis"
              value={stats.veiculosDisponiveis}
              icon={<DirectionsCar sx={{ color: 'white', fontSize: 32 }} />}
              color="#2e7d32"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Vendas do Mês"
              value={stats.vendasMes}
              icon={<AttachMoney sx={{ color: 'white', fontSize: 32 }} />}
              color="#ed6c02"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Lucro do Mês"
              value={formatCurrency(stats.lucroMes)}
              icon={<TrendingUp sx={{ color: 'white', fontSize: 32 }} />}
              color="#9c27b0"
            />
          </Grid>
        </Grid>
      )}

      {/* Prospecções */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Assignment color="primary" />
          <Typography variant="h6" fontWeight="bold">Prospecções</Typography>
        </Box>
        <Grid container spacing={2}>
          {[
            { label: 'Abertas', key: 'aberto', color: '#1976d2' },
            { label: 'Ag. Banco', key: 'aguardando_banco', color: '#ed6c02' },
            { label: 'Ag. Retorno', key: 'aguardando_retorno', color: '#0288d1' },
            { label: 'Fechadas', key: 'fechado', color: '#2e7d32' },
            { label: 'Canceladas', key: 'cancelado', color: '#d32f2f' },
          ].map(({ label, key, color }) => (
            <Grid item xs={6} sm={4} md={2.4} key={key}>
              <Box
                sx={{
                  textAlign: 'center',
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${color}44`,
                  backgroundColor: `${color}11`,
                }}
              >
                <Typography variant="h4" fontWeight="bold" sx={{ color }}>
                  {prospeccoes[key]}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {label}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Galeria de Veículos */}
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" fontWeight="bold">
            Veículos Disponíveis
          </Typography>
        {totalPaginas > 1 && (
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2" color="textSecondary">
              Página {currentPage + 1} de {totalPaginas}
            </Typography>
              <IconButton
                onClick={handlePrevPage}
                disabled={currentPage === 0}
                size="small"
              >
                <ChevronLeft />
              </IconButton>
              <IconButton
                onClick={handleNextPage}
                disabled={(currentPage + 1) * veiculosPorPagina >= veiculos.length}
                size="small"
              >
                <ChevronRight />
              </IconButton>
            </Box>
          )}
        </Box>

        {veiculosExibidos.length === 0 ? (
          <Box textAlign="center" py={5}>
            <DirectionsCar sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="textSecondary">
              Nenhum veículo disponível no momento
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {veiculosExibidos.map((veiculo) => (
              <Grid item xs={12} sm={6} md={4} lg={2.4} key={veiculo.id}>
                <Card elevation={0} sx={{ height: '100%', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <CardActionArea onClick={() => handleVeiculoClick(veiculo.id)}>
                    <CardMedia
                      component="div"
                      sx={{
                        height: 200,
                        backgroundColor: 'rgba(255,255,255,0.04)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                      }}
                    >
                      {veiculo.foto_url ? (
                        <Box
                          component="img"
                          src={veiculo.foto_url}
                          alt={`${veiculo.marca} ${veiculo.modelo}`}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentNode.querySelector('.placeholder').style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <Box
                        className="placeholder"
                        sx={{
                          display: veiculo.foto_url ? 'none' : 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '100%',
                          height: '100%',
                          flexDirection: 'column',
                        }}
                      >
                        <DirectionsCar sx={{ fontSize: 48, color: 'text.disabled' }} />
                        <Typography variant="caption" color="textSecondary">
                          Sem foto
                        </Typography>
                      </Box>
                      <Chip
                        label={getStatusLabel(veiculo.status)}
                        color={getStatusColor(veiculo.status)}
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          fontWeight: 'bold',
                        }}
                      />
                    </CardMedia>
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight="bold" noWrap>
                        {veiculo.marca} {veiculo.modelo}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" noWrap>
                        {veiculo.ano_modelo || '-'} - {veiculo.cor || '-'}
                      </Typography>
                      <Typography
                        variant="h6"
                        color="primary"
                        fontWeight="bold"
                        sx={{ mt: 1 }}
                      >
                        {formatCurrency(veiculo.preco_venda)}
                      </Typography>
                      <Typography variant="caption" color="textSecondary" noWrap>
                        {veiculo.placa}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>
    </Box>
  );
}






